
import React, { useState, useEffect, useCallback } from 'react';
import { useProxyConfig } from '@/contexts/ProxyConfigContext';
import { checkSimpleServiceHealth } from '@/utils/proxy/simpleHealthChecks';
import { ServiceType } from '@/utils/proxy/types';

interface ServiceStatus {
  service: string;
  name: string;
  description: string;
  healthy: boolean;
  checking: boolean;
  proxyEnabled: boolean;
  url: string;
  icon: React.ReactNode;
}

export const useProxyServiceStatus = () => {
  const { config, forceReload, error: configError, loading: configLoading } = useProxyConfig();
  const [serviceStatuses, setServiceStatuses] = useState<ServiceStatus[]>([]);
  const [checking, setChecking] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [lastCheckTime, setLastCheckTime] = useState<number>(0);

  // Функция для получения статуса прокси из динамической конфигурации
  const isProxyEnabledForService = useCallback((service: string): boolean => {
    // Формируем правильные ключи для каждого сервиса
    let enabledKey: string;
    
    switch (service) {
      case 'supabase':
        enabledKey = 'VITE_SUPABASE_PROXY_ENABLED';
        break;
      case 'supabaseStorage':
        enabledKey = 'VITE_SUPABASE_STORAGE_PROXY_ENABLED';
        break;
      case 'googleMaps':
        enabledKey = 'VITE_GOOGLE_MAPS_PROXY_ENABLED';
        break;
      case 'geocoding':
        enabledKey = 'VITE_GEOCODING_PROXY_ENABLED';
        break;
      case 'makeWebhooks':
        enabledKey = 'VITE_MAKE_WEBHOOKS_PROXY_ENABLED';
        break;
      case 'webhooks':
        enabledKey = 'VITE_WEBHOOKS_PROXY_ENABLED';
        break;
      default:
        enabledKey = `VITE_${service.toUpperCase()}_PROXY_ENABLED`;
        break;
    }
    
    const enabled = config[enabledKey] === 'true';
    console.log(`🔧 Прокси для ${service}: ${enabledKey} = ${config[enabledKey]} (${enabled})`);
    return enabled;
  }, [config]);

  // Функция для получения URL сервиса из динамической конфигурации
  const getServiceUrl = useCallback((service: string): string => {
    // Формируем правильные ключи для каждого сервиса
    let urlKey: string;
    
    switch (service) {
      case 'supabase':
        urlKey = 'VITE_SUPABASE_PROXY_URL';
        break;
      case 'supabaseStorage':
        urlKey = 'VITE_SUPABASE_STORAGE_PROXY_URL';
        break;
      case 'googleMaps':
        urlKey = 'VITE_GOOGLE_MAPS_PROXY_URL';
        break;
      case 'geocoding':
        urlKey = 'VITE_GEOCODING_PROXY_URL';
        break;
      case 'makeWebhooks':
        urlKey = 'VITE_MAKE_WEBHOOKS_PROXY_URL';
        break;
      case 'webhooks':
        urlKey = 'VITE_WEBHOOKS_PROXY_URL';
        break;
      default:
        urlKey = `VITE_${service.toUpperCase()}_PROXY_URL`;
        break;
    }
    
    const proxyUrl = config[urlKey];
    
    console.log(`🔧 URL для ${service}: ${urlKey} = ${proxyUrl}`);
    
    if (proxyUrl && isProxyEnabledForService(service)) {
      return proxyUrl;
    }

    // Fallback к стандартным URL
    switch (service) {
      case 'supabase':
      case 'supabaseStorage':
        return 'https://urwzhudyitjovstzqfdj.supabase.co';
      case 'googleMaps':
      case 'geocoding':
        return 'https://maps.googleapis.com';
      case 'makeWebhooks':
        return 'https://hook.eu2.make.com';
      default:
        return '';
    }
  }, [config, isProxyEnabledForService]);

  // Инициализация статусов сервисов
  const initializeServiceStatuses = useCallback(() => {
    console.log('📊 Инициализация статусов сервисов...');
    
    const initialStatuses: ServiceStatus[] = [
      {
        service: 'supabase',
        name: 'Supabase Database',
        description: 'База данных и API',
        healthy: false,
        checking: false,
        proxyEnabled: isProxyEnabledForService('supabase'),
        url: getServiceUrl('supabase'),
        icon: React.createElement('div', { className: 'h-4 w-4 bg-blue-500 rounded' })
      },
      {
        service: 'supabaseStorage',
        name: 'Supabase Storage',
        description: 'Хранилище файлов и изображений',
        healthy: false,
        checking: false,
        proxyEnabled: isProxyEnabledForService('supabaseStorage'),
        url: getServiceUrl('supabaseStorage'),
        icon: React.createElement('div', { className: 'h-4 w-4 bg-green-500 rounded' })
      },
      {
        service: 'googleMaps',
        name: 'Google Maps API',
        description: 'Карты и геокодирование',
        healthy: false,
        checking: false,
        proxyEnabled: isProxyEnabledForService('googleMaps'),
        url: getServiceUrl('googleMaps'),
        icon: React.createElement('div', { className: 'h-4 w-4 bg-red-500 rounded' })
      },
      {
        service: 'geocoding',
        name: 'Geocoding API',
        description: 'Определение адресов по координатам',
        healthy: false,
        checking: false,
        proxyEnabled: isProxyEnabledForService('geocoding'),
        url: getServiceUrl('geocoding'),
        icon: React.createElement('div', { className: 'h-4 w-4 bg-yellow-500 rounded' })
      },
      {
        service: 'makeWebhooks',
        name: 'Make.com Webhooks',
        description: 'Автоматизация и интеграции',
        healthy: false,
        checking: false,
        proxyEnabled: isProxyEnabledForService('makeWebhooks'),
        url: getServiceUrl('makeWebhooks'),
        icon: React.createElement('div', { className: 'h-4 w-4 bg-purple-500 rounded' })
      }
    ];

    setServiceStatuses(initialStatuses);
    setInitialized(true);
    
    console.log('📊 Статусы сервисов инициализированы:', initialStatuses.map(s => ({
      service: s.service,
      proxyEnabled: s.proxyEnabled,
      url: s.url
    })));
  }, [isProxyEnabledForService, getServiceUrl]);

  // Проверка всех сервисов с ограничением частоты
  const checkAllServices = useCallback(async () => {
    if (!initialized) {
      console.log('⏳ Сервисы еще не инициализированы, пропускаем проверку');
      return serviceStatuses;
    }

    // Ограничиваем частоту проверок (не чаще чем раз в 10 секунд)
    const now = Date.now();
    if (now - lastCheckTime < 10000) {
      console.log('⏳ Слишком частые проверки, пропускаем');
      return serviceStatuses;
    }

    setChecking(true);
    setLastCheckTime(now);
    console.log('🔍 Начинаем проверку всех сервисов...');
    
    const updatedStatuses = await Promise.all(
      serviceStatuses.map(async (status) => {
        const newStatus = { ...status, checking: true };
        setServiceStatuses(prev => 
          prev.map(s => s.service === status.service ? newStatus : s)
        );

        try {
          console.log(`🔍 Проверяем ${status.service} (прокси: ${status.proxyEnabled})`);
          const healthy = await checkSimpleServiceHealth(status.service as ServiceType);
          console.log(`📊 Результат для ${status.service}: ${healthy ? 'доступен' : 'недоступен'}`);
          return { ...newStatus, healthy, checking: false };
        } catch (error) {
          console.error(`❌ Ошибка проверки ${status.service}:`, error);
          return { ...newStatus, healthy: false, checking: false };
        }
      })
    );

    setServiceStatuses(updatedStatuses);
    setChecking(false);
    
    console.log('✅ Проверка всех сервисов завершена');
    return updatedStatuses;
  }, [serviceStatuses, initialized, lastCheckTime]);

  // Проверка конкретного сервиса
  const checkSingleService = useCallback(async (serviceName: string) => {
    console.log(`🔍 Проверяем отдельный сервис: ${serviceName}`);
    
    setServiceStatuses(prev =>
      prev.map(s => 
        s.service === serviceName 
          ? { ...s, checking: true }
          : s
      )
    );

    try {
      const healthy = await checkSimpleServiceHealth(serviceName as ServiceType);
      console.log(`📊 Результат для ${serviceName}: ${healthy ? 'доступен' : 'недоступен'}`);
      
      setServiceStatuses(prev =>
        prev.map(s => 
          s.service === serviceName 
            ? { ...s, healthy, checking: false }
            : s
        )
      );
      
      return healthy;
    } catch (error) {
      console.error(`❌ Ошибка проверки ${serviceName}:`, error);
      setServiceStatuses(prev =>
        prev.map(s => 
          s.service === serviceName 
            ? { ...s, healthy: false, checking: false }
            : s
        )
      );
      return false;
    }
  }, []);

  // Принудительное обновление с перезагрузкой конфигурации
  const refreshStatuses = useCallback(async () => {
    console.log('🔄 Принудительное обновление статусов сервисов...');
    setInitialized(false);
    setLastCheckTime(0); // Сбрасываем ограничение частоты
    await forceReload();
    // Инициализация произойдет автоматически через useEffect
  }, [forceReload]);

  // Инициализация при загрузке или изменении конфигурации
  useEffect(() => {
    if (!configLoading && Object.keys(config).length > 0 && !initialized) {
      console.log('🔄 Конфигурация загружена, инициализируем статусы сервисов');
      initializeServiceStatuses();
    }
  }, [config, configLoading, initializeServiceStatuses, initialized]);

  // Автоматическая проверка после инициализации (с задержкой)
  useEffect(() => {
    if (initialized && serviceStatuses.length > 0) {
      console.log('🔄 Статусы инициализированы, запускаем проверку через 3 секунды...');
      const timer = setTimeout(() => {
        checkAllServices();
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [initialized, serviceStatuses.length]); // Убираем checkAllServices из зависимостей чтобы избежать циклов

  return {
    serviceStatuses,
    checking,
    checkAllServices,
    checkSingleService,
    refreshStatuses,
    configError,
    initialized
  };
};
